import { Alert, Flex, Modal, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import type { OutboundWebhookTestResult } from 'src/api/main'
import { CodeSnippet } from 'src/components'

const { Text } = Typography

type Props = {
  /** null keeps the modal closed; a result opens it. */
  result: OutboundWebhookTestResult | null
  onClose: () => void
}

/**
 * The outcome of one test delivery.
 *
 * Shows what the endpoint actually answered rather than our interpretation of it. A refused URL,
 * a timeout and an HTTP 404 all arrive as the same shape with `ok` telling them apart, so they
 * render in one place -- and the consumer's own words are the part that resolves the problem:
 * n8n answers a disarmed test URL with the sentence that says to press "Execute workflow".
 */
export const WebhookTestResultModal = ({ result, onClose }: Props) => {
  const { t } = useTranslation()

  return (
    <Modal
      open={result !== null}
      onCancel={onClose}
      footer={null}
      title={t('workspace.apps.outboundWebhooks.test.title', 'Test delivery')}
    >
      {result ? (
        <Flex vertical gap={12}>
          <Alert
            showIcon
            type={result.ok ? 'success' : 'error'}
            title={
              result.ok
                ? t(
                    'workspace.apps.outboundWebhooks.test.accepted',
                    'The endpoint accepted the delivery ({{status}}).',
                    { status: result.status_code }
                  )
                : result.status_code
                  ? t(
                      'workspace.apps.outboundWebhooks.test.rejected',
                      'The endpoint answered {{status}}.',
                      { status: result.status_code }
                    )
                  : t(
                      'workspace.apps.outboundWebhooks.test.unreachable',
                      'The endpoint could not be reached.'
                    )
            }
            description={result.error ?? undefined}
          />

          {result.response_body ? (
            <Flex vertical gap={6}>
              <Text type="secondary">
                {t(
                  'workspace.apps.outboundWebhooks.test.responseBody',
                  'Response from the endpoint'
                )}
              </Text>
              <CodeSnippet>{result.response_body}</CodeSnippet>
            </Flex>
          ) : null}

          <Text type="secondary">
            {t(
              'workspace.apps.outboundWebhooks.test.hint',
              'The request is signed exactly like a real event, with the event name webhook.test. Some tools arm their test URL for a single call — press their listen button, then send again.'
            )}
          </Text>

          {/* Shown on success too: the receiver logs it as X-CRM-Delivery, which is how the
              two sides talk about the same request when something needs chasing. */}
          <Text type="secondary">
            {t(
              'workspace.apps.outboundWebhooks.test.deliveryId',
              'Delivery: {{id}}',
              { id: result.delivery_id }
            )}
          </Text>
        </Flex>
      ) : null}
    </Modal>
  )
}
